'use strict';

/**
 * What the sample Documents rail says about each document's media. The test logic it used to hold (test codes, the
 * tests each form declares, results, story order) is in @paviasystems/headlight-samples (SampleTest-Rules.js).
 */

/**
 * The kind of media an observation carries, or '' for none. Same precedence as
 * lib/web/media-detail-cards/source/MediaCardBuilder.js curateMediaObservationList (image over audio over video, a file
 * only when none of those); keep the two in step.
 *
 * @param {Object} pObservation
 * @return {String} 'image' | 'audio' | 'video' | 'file' | ''
 */
function getMediaKind(pObservation)
{
	const tmpDetails = (pObservation && pObservation.Details) || {};
	const fHas = (pKey) => !!(tmpDetails[pKey] && tmpDetails[pKey][0] && (String(tmpDetails[pKey][0].ArtifactType || '').toLowerCase() === pKey.toLowerCase()));
	if (fHas('Image'))
	{
		return 'image';
	}
	if (fHas('Audio'))
	{
		return 'audio';
	}
	if (fHas('Video'))
	{
		return 'video';
	}
	return (tmpDetails.File && tmpDetails.File[0]) ? 'file' : '';
}

/**
 * Count each document's media observations.
 *
 * @param {Array<{ IDDocument: number, IDObservation: number }>} pJoins - DocumentObservationJoin rows
 * @param {Array<Object>} pObservations - the joined observations that are not deleted
 * @return {Record<number, Record<String, number>>} IDDocument -> media kind -> count
 */
function countDocumentMedia(pJoins, pObservations)
{
	const tmpKindByObservation = {};
	for (const tmpObservation of (pObservations || []))
	{
		tmpKindByObservation[tmpObservation.IDObservation] = getMediaKind(tmpObservation);
	}
	const tmpCounts = {};
	const tmpSeen = {};
	for (const tmpJoin of (pJoins || []))
	{
		const tmpKind = tmpKindByObservation[tmpJoin.IDObservation];
		const tmpKey = `${tmpJoin.IDDocument}:${tmpJoin.IDObservation}`;
		if (!tmpKind || tmpSeen[tmpKey])
		{
			continue;
		}
		tmpSeen[tmpKey] = true;
		const tmpDocument = tmpCounts[tmpJoin.IDDocument] || (tmpCounts[tmpJoin.IDDocument] = {});
		tmpDocument[tmpKind] = (tmpDocument[tmpKind] || 0) + 1;
	}
	return tmpCounts;
}

/**
 * The photos and videos joined to some documents, each observation once, oldest first.
 *
 * @param {Array<{ IDDocument: number, IDObservation: number }>} pJoins - DocumentObservationJoin rows
 * @param {Array<Object>} pObservations - the joined observations the reader could read
 * @return {Array<{ IDObservation: number, Name: String, Kind: String, When: String }>}
 */
function listDocumentMedia(pJoins, pObservations)
{
	const tmpJoined = new Set((pJoins || []).map((pJoin) => Number(pJoin.IDObservation)));
	const tmpSeen = new Set();
	const tmpMedia = [];
	for (const tmpObservation of (pObservations || []))
	{
		const tmpID = Number(tmpObservation && tmpObservation.IDObservation);
		const tmpKind = getMediaKind(tmpObservation);
		if (!tmpJoined.has(tmpID) || tmpSeen.has(tmpID) || ((tmpKind !== 'image') && (tmpKind !== 'video')))
		{
			continue;
		}
		tmpSeen.add(tmpID);
		tmpMedia.push({ IDObservation: tmpID, Name: tmpObservation.Name || '', Kind: tmpKind, When: tmpObservation.DesignatedTime || tmpObservation.CreateDate || '' });
	}
	return tmpMedia.sort((pA, pB) => (String(pA.When).localeCompare(String(pB.When))) || (pA.IDObservation - pB.IDObservation));
}

module.exports = { getMediaKind, countDocumentMedia, listDocumentMedia };
